import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Eye } from "lucide-react";
import { Button } from "@headerhawk/ui";
import { useAuthSession } from "@/components/providers/auth-context";
import { DemoGate } from "@/components/demo/demo-gate";
import {
  endImpersonation,
  isDemoTab,
  isImpersonationTab,
  markImpersonationEnded,
  readImpersonationState,
  type ImpersonationState,
} from "@/lib/auth/impersonation";
import { DESCOPE_PROJECT } from "@/lib/auth/descope-project";
import { getRefreshToken } from "@/lib/auth/token-manager";
import { useBannerOffset } from "@/lib/hooks/use-banner-offset";
import { useProvisioning } from "@/lib/hooks/use-provisioning";

/** How often the minutes left are redrawn. */
const TICK_MS = 15_000;

/**
 * An operator's read-only view of this dashboard as one member (task 394).
 *
 * In an ordinary tab this renders its children and nothing else. In an
 * impersonation tab it puts a banner that cannot be dismissed above the whole
 * app, ends the session at its expiry, and once it has ended — by the clock,
 * by **End session**, or by Descope refusing the token — replaces the app with
 * a page that says so. Never the login screen: that would invite the operator
 * to sign in as themselves in a tab whose storage says it is someone else's.
 */
export function ImpersonationGate({ children }: { children: ReactNode }) {
  if (!isImpersonationTab()) return <>{children}</>;
  // The public demo (task 405) is an impersonation tab of its own kind.
  if (isDemoTab()) return <DemoGate>{children}</DemoGate>;
  return <ImpersonatedApp>{children}</ImpersonatedApp>;
}

function ImpersonatedApp({ children }: { children: ReactNode }) {
  const session = useAuthSession();
  const [state, setState] = useState<ImpersonationState | null>(
    readImpersonationState,
  );
  const [now, setNow] = useState(() => Date.now());

  const end = useCallback(async (reason: "expired" | "ended") => {
    await endImpersonation(DESCOPE_PROJECT, reason);
    setState(readImpersonationState());
  }, []);

  const expiry = state?.expiresAt ? Date.parse(state.expiresAt) : NaN;
  const live = state !== null && state.ended === null;

  // End the session at its expiry, whatever the operator is doing.
  useEffect(() => {
    if (!live) return;
    const remaining = expiry - Date.now();
    if (!Number.isFinite(remaining) || remaining <= 0) {
      void end("expired");
      return;
    }
    const timer = setTimeout(() => void end("expired"), remaining);
    const tick = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => {
      clearTimeout(timer);
      clearInterval(tick);
    };
  }, [live, expiry, end]);

  // No session. If the token layer cleared the refresh token, Descope refused
  // it (revoked, or its member disabled) and the session is over for good.
  // If the token is still here, the refresh failed for another reason — a
  // 429, a Descope 5xx, the network — which a reload can recover from, so
  // nothing is recorded as ended.
  const unauthenticated =
    live && !session.isSessionLoading && !session.isAuthenticated;
  const refused = unauthenticated && getRefreshToken() === null;
  useEffect(() => {
    if (refused) {
      markImpersonationEnded("ended");
      setState(readImpersonationState());
    }
  }, [refused]);

  if (!state || state.ended !== null || refused) {
    return <ImpersonationEnded reason={state?.ended ?? "ended"} />;
  }
  if (unauthenticated) {
    return <ImpersonationUnavailable />;
  }

  const minutesLeft = Math.max(1, Math.ceil((expiry - now) / 60_000));
  return (
    <>
      <ImpersonationBanner
        operatorEmail={state.operatorEmail}
        minutesLeft={minutesLeft}
        onEnd={() => void end("ended")}
      />
      {children}
    </>
  );
}

function ImpersonationBanner({
  operatorEmail,
  minutesLeft,
  onEnd,
}: {
  operatorEmail: string | null;
  minutesLeft: number;
  onEnd: () => void;
}) {
  const { profile } = useProvisioning();
  const [ending, setEnding] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);
  useBannerOffset(bannerRef);
  const member = profile?.user.email;
  const organisation = profile?.tenant.name;

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label="Operator view"
      data-impersonation-banner=""
      className="sticky top-0 z-50 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-amber-500/40 bg-amber-100 px-4 py-2 text-sm text-amber-950 dark:bg-amber-950 dark:text-amber-50"
    >
      <Eye className="h-4 w-4 shrink-0" aria-hidden="true" />
      <p className="min-w-0 flex-1">
        <strong>Read-only view</strong> as{" "}
        <span data-impersonated-member="">{member ?? "this member"}</span>
        {organisation ? (
          <>
            {" "}
            in <span data-impersonated-organisation="">{organisation}</span>
          </>
        ) : null}
        . Nothing here can be changed.{" "}
        {operatorEmail ? `Opened by ${operatorEmail}. ` : null}
        <span data-impersonation-minutes-left="">
          Ends in {minutesLeft} {minutesLeft === 1 ? "minute" : "minutes"}.
        </span>
      </p>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={ending}
        onClick={() => {
          setEnding(true);
          onEnd();
        }}
      >
        {ending ? "Ending…" : "End session"}
      </Button>
    </div>
  );
}

const ENDED_COPY: Record<NonNullable<ImpersonationState["ended"]>, string> = {
  expired: "This read-only view reached its 30 minutes and has ended.",
  ended: "This read-only view has ended.",
  invalid:
    "This link does not carry a session, or it has already been used up. Open a new view from the admin console.",
};

function ImpersonationEnded({
  reason,
}: {
  reason: NonNullable<ImpersonationState["ended"]>;
}) {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center">
      <h1 className="text-xl font-semibold">This session has ended</h1>
      <p className="max-w-md text-muted-foreground">{ENDED_COPY[reason]}</p>
      <p className="max-w-md text-sm text-muted-foreground">
        You can close this tab. To look again, open a new view from the admin
        console; each one is recorded with its reason.
      </p>
    </main>
  );
}

function ImpersonationUnavailable() {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center">
      <h1 className="text-xl font-semibold">
        The session could not be checked
      </h1>
      <p className="max-w-md text-muted-foreground">
        The sign-in service did not answer. This read-only view has not ended;
        try again in a moment.
      </p>
      <Button type="button" onClick={() => window.location.reload()}>
        Try again
      </Button>
    </main>
  );
}
